import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard,
  DollarSign,
  Users,
  TrendingUp,
  Calendar,
  Search,
  Filter,
  Download,
  CheckCircle,
  Clock,
  XCircle,
  Mail,
  Eye,
  CheckSquare,
  Square,
  AlertCircle,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import adminApiService from '../../services/adminApiService';

const PaymentsModule = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Local search state for input (not debounced)
  const [searchInput, setSearchInput] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    program: 'all',
    status: 'all',
    dateRange: '30',
    search: '',
    sortBy: 'paymentDate',
    sortOrder: 'desc'
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });

  // Load payments data
  const loadPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      const response = await adminApiService.payments.getPayments(params);
      setPayments(response.data.payments);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading payments:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await adminApiService.payments.getPaymentStats(filters.dateRange);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Mark as paid
  const handleMarkAsPaid = async (email, program) => {
    try {
      setActionLoading(true);
      await adminApiService.payments.markAsPaid(email, program);
      setShowMarkPaidModal(false);
      setSelectedPayment(null);
      loadPayments(); // Refresh data
      loadStats(); // Refresh stats
    } catch (error) {
      console.error('Error marking as paid:', error);
      setError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk mark as paid
  const handleBulkMarkAsPaid = async () => {
    try {
      setActionLoading(true);
      const students = selectedPayments.map(payment => ({
        email: payment.originalEmail,
        program: payment.program === 'Study Abroad' ? 'study' : 'work'
      }));
      
      await adminApiService.payments.bulkMarkAsPaid(students);
      setShowBulkModal(false);
      setSelectedPayments([]);
      loadPayments(); // Refresh data
      loadStats(); // Refresh stats
    } catch (error) {
      console.error('Error in bulk mark as paid:', error);
      setError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Export payments
  const handleExport = async () => {
    try {
      const blob = await adminApiService.payments.exportPayments(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting payments:', error);
      setError(error.message);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Handle selection
  const handleSelectPayment = (payment) => {
    setSelectedPayments(prev => {
      const isSelected = prev.some(p => p._id === payment._id);
      if (isSelected) {
        return prev.filter(p => p._id !== payment._id);
      } else {
        return [...prev, payment];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPayments.length === payments.length) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(payments);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'unpaid':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} />;
      case 'unpaid':
        return <XCircle size={16} />;
      case 'pending':
        return <Clock size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  // Debounced search effect - updates filters.search after user stops typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on search
    }, 500); // 500ms delay

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  // Load data on mount and when filters change
  useEffect(() => {
    loadPayments();
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    loadStats();
  }, [filters.dateRange]);

  if (loading && payments.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F8FA] to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202356] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payments Tracking</h1>
        <p className="text-gray-600">Manage and track all student payments</p>
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
        >
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-600 hover:text-red-800 mt-1"
            >
              Dismiss
            </button>
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">${stats.totalRevenue.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <DollarSign className="text-green-600" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Paid Students</p>
                <p className="text-2xl font-bold text-gray-900">{stats.paidStudents}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <CheckCircle className="text-blue-600" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Unpaid Students</p>
                <p className="text-2xl font-bold text-gray-900">{stats.unpaidStudents}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <XCircle className="text-red-600" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Students</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <Users className="text-purple-600" size={24} />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Search Bar - Always Visible */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <button
            onClick={() => {
              setSearchInput('');
              setFilters({
                program: 'all',
                status: 'all',
                dateRange: '30',
                search: '',
                sortBy: 'paymentDate',
                sortOrder: 'desc'
              });
            }}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Reset Filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Program</label>
            <select
              value={filters.program}
              onChange={(e) => handleFilterChange('program', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
            >
              <option value="all">All Programs</option>
              <option value="study">Study Abroad</option>
              <option value="work">Work Abroad</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                handleFilterChange('sortBy', sortBy);
                handleFilterChange('sortOrder', sortOrder);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
            >
              <option value="paymentDate-desc">Date (Newest First)</option>
              <option value="paymentDate-asc">Date (Oldest First)</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="status-asc">Status (A-Z)</option>
              <option value="status-desc">Status (Z-A)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
            >
              {selectedPayments.length === payments.length && payments.length > 0 ? (
                <CheckSquare size={20} />
              ) : (
                <Square size={20} />
              )}
              Select All ({selectedPayments.length})
            </button>

            {selectedPayments.length > 0 && (
              <button
                onClick={() => setShowBulkModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle size={16} />
                Mark Selected as Paid
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadPayments}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table View - Hidden on Mobile */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center justify-center w-5 h-5 border border-gray-300 rounded hover:bg-gray-100"
                  >
                    {selectedPayments.length === payments.length && payments.length > 0 ? (
                      <CheckCircle size={16} className="text-blue-600" />
                    ) : (
                      <div className="w-4 h-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Program
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transformed Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {payments.map((payment, index) => (
                <motion.tr
                  key={payment._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleSelectPayment(payment)}
                      className="flex items-center justify-center w-5 h-5 border border-gray-300 rounded hover:bg-gray-100"
                    >
                      {selectedPayments.some(p => p._id === payment._id) ? (
                        <CheckCircle size={16} className="text-blue-600" />
                      ) : (
                        <div className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{payment.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      payment.program === 'Study Abroad' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {payment.program}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{payment.originalEmail}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">{payment.transformedEmail}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">${payment.amount}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>
                      {getStatusIcon(payment.status)}
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">
                      {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => window.open(`mailto:${payment.originalEmail}`, '_blank')}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Send Email"
                      >
                        <Mail size={16} />
                      </button>
                      {payment.status !== 'paid' && (
                        <button
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowMarkPaidModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-green-600"
                          title="Mark as Paid"
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {payments.length === 0 && !loading && (
          <div className="text-center py-8">
            <CreditCard className="text-gray-400 mx-auto mb-2" size={48} />
            <p className="text-gray-500">No payments found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Mobile Card View - Hidden on Desktop */}
      <div className="md:hidden space-y-4">
        {payments.length === 0 && !loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <CreditCard className="text-gray-400 mx-auto mb-2" size={48} />
            <p className="text-gray-500">No payments found matching your criteria</p>
          </div>
        ) : (
          payments.map((payment, index) => (
            <motion.div
              key={payment._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Card Header */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleSelectPayment(payment)}
                    className="flex items-center justify-center w-5 h-5 border border-gray-300 rounded hover:bg-gray-100"
                  >
                    {selectedPayments.some(p => p._id === payment._id) ? (
                      <CheckCircle size={16} className="text-blue-600" />
                    ) : (
                      <div className="w-4 h-4" />
                    )}
                  </button>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    payment.program === 'Study Abroad' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {payment.program}
                  </span>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>
                  {getStatusIcon(payment.status)}
                  {payment.status}
                </span>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-3">
                {/* Name */}
                <div>
                  <div className="text-xs text-gray-500 mb-1">Name</div>
                  <div className="font-semibold text-gray-900">{payment.name}</div>
                </div>

                {/* Email */}
                <div>
                  <div className="text-xs text-gray-500 mb-1">Email</div>
                  <div className="text-sm text-gray-900 break-all">{payment.originalEmail}</div>
                </div>

                {/* Transformed Email */}
                <div>
                  <div className="text-xs text-gray-500 mb-1">Transformed Email</div>
                  <div className="text-sm text-gray-600 break-all">{payment.transformedEmail}</div>
                </div>

                {/* Amount and Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Amount</div>
                    <div className="font-semibold text-gray-900">${payment.amount}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Payment Date</div>
                    <div className="text-sm text-gray-600">
                      {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Actions */}
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-2">
                <button
                  onClick={() => window.open(`mailto:${payment.originalEmail}`, '_blank')}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-white transition-colors"
                >
                  <Mail size={16} />
                  Email
                </button>
                {payment.status !== 'paid' && (
                  <button
                    onClick={() => {
                      setSelectedPayment(payment);
                      setShowMarkPaidModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle size={16} />
                    Mark as Paid
                  </button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Shared Pagination for Both Views */}
      {payments.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6">
          <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Items per page:</label>
                <select
                  value={pagination.limit}
                  onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202356]"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Mark as Paid Modal */}
      {showMarkPaidModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Mark as Paid</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Student</p>
                  <p className="font-medium text-gray-900">{selectedPayment.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Program</p>
                  <p className="font-medium text-gray-900">{selectedPayment.program}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-900">{selectedPayment.originalEmail}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Amount</p>
                  <p className="font-medium text-gray-900">${selectedPayment.amount}</p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  This will mark the student as paid and send a payment receipt to their email address.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowMarkPaidModal(false);
                  setSelectedPayment(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleMarkAsPaid(selectedPayment.originalEmail, selectedPayment.program === 'Study Abroad' ? 'study' : 'work')}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Mark as Paid'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bulk Mark as Paid Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Bulk Mark as Paid</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                You are about to mark {selectedPayments.length} students as paid:
              </p>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {selectedPayments.map((payment) => (
                  <div key={payment._id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{payment.name}</span>
                    <span className="text-sm text-gray-600">${payment.amount}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  This will mark all selected students as paid and send payment receipts to their email addresses.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setSelectedPayments([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkMarkAsPaid}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : `Mark ${selectedPayments.length} as Paid`}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PaymentsModule;
